import { startTransition, useEffect, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { useRecoilValue } from "recoil";

import {
  useCrudEditContext,
  useDropdownSourceContext,
  usePageContext,
} from "@ryde/redux";
import {
  DirectionEnum,
  DtoFieldType,
  FORM_FIELD_VALUES,
  formTabControlsSF,
  ICustomField,
  IDropdownItem,
  IExtendableDto,
  InfluencedByResolverNameEnum,
  ISelectListItem,
  ISelectListItemExt,
  mergeDateWithHHmmTime,
  TimeZoneEnum,
  TTimeHHmm,
  useTimezone,
} from "@ryde/shared";

interface IResolveParams {
  influencedByValueKey: string;
  formFieldTypeId: FORM_FIELD_VALUES | null | undefined;
  foundItem: ISelectListItemExt;
  currentValue: unknown;
  timezone: TimeZoneEnum;
}

const resolveNewValueByFormFieldTypeId = ({
  influencedByValueKey,
  foundItem,
}: IResolveParams): DtoFieldType => {
  // TBD
  return (foundItem as ISelectListItemExt)[influencedByValueKey];
};

const bookingBookTimeByShiftResolver = ({
  foundItem,
  currentValue,
  timezone,
}: IResolveParams): DtoFieldType => {
  let resolvedShiftTime: TTimeHHmm | null = null;

  switch (foundItem.direction) {
    case DirectionEnum.PickUp:
      resolvedShiftTime = foundItem.shiftStartTime as TTimeHHmm;
      break;
    case DirectionEnum.DropOff:
      resolvedShiftTime = foundItem.shiftEndTime as TTimeHHmm;
      break;
    default:
      console.warn(`Unexpected direction: ${foundItem.direction}`);
      return currentValue as TDateISO;
  }

  return mergeDateWithHHmmTime(
    currentValue as TDateISO,
    resolvedShiftTime,
    timezone,
  );
};

const influencedByResolvers: Record<
  InfluencedByResolverNameEnum,
  (args: IResolveParams) => DtoFieldType
> = {
  [InfluencedByResolverNameEnum.BOOKING_BOOK_TIME_BY_SHIFT]:
    bookingBookTimeByShiftResolver,
};

const resolveInfluencedByNewValue = ({
  influencedByResolverName,
  foundItem,
  influencedByValueKey,
  formFieldTypeId,
  currentValue,
  timezone,
}: {
  influencedByValueKey: string;
  formFieldTypeId: FORM_FIELD_VALUES | null | undefined;
  influencedByResolverName: InfluencedByResolverNameEnum | undefined;
  foundItem: ISelectListItemExt;
  currentValue: unknown;
  timezone: TimeZoneEnum;
}): DtoFieldType => {
  if (influencedByResolverName) {
    const resolver = influencedByResolvers[influencedByResolverName];
    return resolver({
      foundItem,
      influencedByValueKey,
      formFieldTypeId,
      currentValue,
      timezone,
    });
  }

  return resolveNewValueByFormFieldTypeId({
    foundItem,
    influencedByValueKey,
    formFieldTypeId,
    currentValue,
    timezone,
  });
};

interface IUseInfluencedInput {
  formAttributes: ICustomField["formAttributes"];
  name: ICustomField["name"];
  formFieldTypeId?: FORM_FIELD_VALUES | null;
}

export const useInfluencedInput = ({
  formAttributes,
  name,
  formFieldTypeId,
}: IUseInfluencedInput) => {
  const { setValue, watch, getValues } = useFormContext();
  const { entity } = usePageContext();
  const { getDropdownItems } = useDropdownSourceContext();
  const { timezone } = useTimezone();
  const {
    data: { isEdit },
  } = useCrudEditContext<IExtendableDto>();
  const influencedItemsRef = useRef<
    IDropdownItem[] | ISelectListItem[] | undefined
  >([]);

  const currentTabControls = useRecoilValue(formTabControlsSF(entity));
  const sourceField = currentTabControls.find(
    control => control.name === formAttributes?.influencedBy?.controlName,
  );
  const isSelect = Boolean(sourceField?.formAttributes?.selectItems);
  const isDropDown = Boolean(sourceField?.formAttributes?.dropItemsSource);

  const watchedSourceField = formAttributes?.influencedBy?.controlName
    ? watch(formAttributes?.influencedBy?.controlName)
    : undefined;
  const watchedSourceFieldRef = useRef<unknown>(undefined);

  useEffect(() => {
    /*
     * Check is it initial setup for this field
     * on edit we load values for form and setting them triggers formAttributes
     * changes
     * so we want to be sure that we are watching changes of observable,
     * not watchedSourceField changed from `undefined` to observable
     *  */
    if (watchedSourceFieldRef.current === undefined) {
      watchedSourceFieldRef.current = watchedSourceField;

      if (isEdit) {
        return;
      }
    }

    if (!influencedItemsRef.current?.length) {
      return;
    }

    if (isSelect) {
      const foundItem = influencedItemsRef.current?.find(
        item => item.value === watchedSourceField,
      );

      if (!foundItem) {
        return;
      }

      const newValue = foundItem.value;

      setValue(name, newValue, {
        shouldDirty: true,
        shouldValidate: true,
      });
    } else if (isDropDown) {
      const foundItem = influencedItemsRef.current?.find(
        //todo check if id is correct name - may be value? (this is not used currently in any control)
        item => (item as ISelectListItemExt).id === watchedSourceField,
      );

      if (!foundItem) {
        return;
      }

      const newValue = resolveInfluencedByNewValue({
        foundItem: foundItem as ISelectListItemExt,
        influencedByValueKey: String(formAttributes?.influencedBy?.valueKey),
        formFieldTypeId,
        influencedByResolverName: formAttributes?.influencedBy?.resolverName,
        currentValue: getValues(name),
        timezone,
      });

      if (!newValue) {
        return;
      }

      setValue(name, newValue, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [
    formAttributes?.influencedBy?.resolverName,
    formAttributes?.influencedBy?.valueKey,
    formFieldTypeId,
    getValues,
    isDropDown,
    isEdit,
    isSelect,
    name,
    setValue,
    timezone,
    watchedSourceField,
  ]);

  useEffect(() => {
    const initial = async (): Promise<void> => {
      if (isSelect) {
        influencedItemsRef.current = sourceField?.formAttributes?.selectItems;
      } else if (isDropDown) {
        const sourceFieldName = sourceField?.formAttributes?.dropItemsSource;

        if (!sourceFieldName) {
          return;
        }

        influencedItemsRef.current = await getDropdownItems(sourceFieldName);
      }
    };

    startTransition(() => {
      initial();
    });
  }, [
    currentTabControls,
    formAttributes?.influencedBy?.controlName,
    getDropdownItems,
    isDropDown,
    isSelect,
    name,
    sourceField?.formAttributes?.dropItemsSource,
    sourceField?.formAttributes?.selectItems,
  ]);
};
